import { useState, useContext, useEffect } from 'react';
import Stack from 'react-bootstrap/Stack';
import Button from 'react-bootstrap/Button';
import { Link, useNavigate } from "react-router-dom";
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Label from 'components/formElements/Label';
import Switch from 'components/formElements/Switch';
import SelectableCard from 'components/formElements/SelectableCard';
import { FormContext } from 'context/FormContext';
import { ReactComponent as IconBattery } from 'assets/images/icon-batterie.svg';
import { ReactComponent as IconStorage } from 'assets/images/icon-speicher.svg';
import { ReactComponent as IconWallbox } from 'assets/images/icon-wallbox.svg';

const defaultContact = {
  HasPhotovoltaik: false,
  HasBatteryStorage: false,
  HasWallbox: false,
  BatterystorageManufactorer: "e3dc",
  InterestInStorage: false,
  InterestInBattery: false,
  InterestInWallbox: false,
};

const Contact = () => {

    const { formValues, setFormValues } = useContext(FormContext);    
    const [contact, setContact] = useState(() => ({
        ...defaultContact,
        ...formValues.contact
    }));
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    const handleUserInput = (e) => {
        const { name, type, checked, value } = e.target;
        const finalValue = type === "checkbox" ? checked : value;
        setContact(prev => ({
            ...prev,
            [name]: finalValue,
        }));
        validateField(name, finalValue);
    }

    const handleToggleInput = (name, value) => {          
        setContact(prev => ({
            ...prev,
            [name]: value,
        }));
    }

    const validateField = (name, value) => {
        let newErrors = { ...errors };

        switch (name) {
            case 'Firstname':
                if (!value) {
                    newErrors.Firstname = "Vorname ist erforderlich";
                } else {
                    delete newErrors.Firstname;
                }
                break;
            case 'Lastname':
                if (!value) {
                    newErrors.Lastname = "Nachname ist erforderlich";
                } else {
                    delete newErrors.Lastname;
                }
                break;
            case 'Email':
                if (!value || !/\S+@\S+\.\S+/.test(value)) {
                    newErrors.Email = "Bitte geben Sie eine gültige E-Mail-Adresse ein";
                } else {
                    delete newErrors.Email;
                }
                break;
            case 'Phone':
                if (!value) {
                    newErrors.Phone = "Telefonnummer ist erforderlich";
                } else {
                    delete newErrors.Phone;
                }
                break;
            case 'Partner':
                if (!value) {
                    newErrors.Partner = "Bitte akzeptieren Sie die Partnerbedingungen";
                } else {
                    delete newErrors.Partner;
                }
                break;
            case 'Legal':
                if (!value) {
                    newErrors.Legal = "Bitte akzeptieren Sie die Datenschutzerklärung";
                } else {
                    delete newErrors.Legal;
                }
                break;
            default:
                break;
        }

        setErrors(newErrors);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!contact.Firstname) {
            newErrors.Firstname = "Vorname ist erforderlich";
        }
        if (!contact.Lastname) {
            newErrors.Lastname = "Nachname ist erforderlich";
        }
        if (!contact.Email || !/\S+@\S+\.\S+/.test(contact.Email)) {
            newErrors.Email = "Bitte geben Sie eine gültige E-Mail-Adresse ein";
        }
        if (!contact.Phone) {
            newErrors.Phone = "Telefonnummer ist erforderlich";
        }
        if (!contact.Partner) {
            newErrors.Partner = "Bitte akzeptieren Sie die Partnerbedingungen";
        }
        if (!contact.Legal) {
            newErrors.Legal = "Bitte akzeptieren Sie die Datenschutzerklärung";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const checkContactForm = () => {
        if (validateForm()) {
            setFormValues({
                ...formValues,
                contact: {
                    ...(formValues.contact ?? {}),
                    ...contact,
                    LeadId: formValues.contact?.LeadId ?? contact.LeadId ?? null,
                    IsCampaignLead: formValues.contact?.IsCampaignLead ?? contact.IsCampaignLead ?? false
                }
            });
            navigate("/SimulationRequest");
        }
    }

    return (
        <Stack gap={3} className="ContainerPaddingSmall">
            <Row className='mb-1'>               
            <h1 className="text-center mb-1">Noch ein Schritt</h1>
            <div className="text-center fs-6">Bis Sie Ihr Auswertungsergebnis erhalten.</div>
            </Row>
            <Row md="2" xs="1" className="mb-3">
                <Col>
                    <Label text="Vorname" />
                    <Form.Control
                        type="text"
                        name="Firstname"
                        aria-describedby="Vorname"
                        value={contact.Firstname || ""}
                        onChange={handleUserInput}
                        isInvalid={!!errors.Firstname}
                    />
                </Col>
                <Col className="mt-4 mt-md-0">
                    <Label text="Nachname" />
                    <Form.Control
                        type="text"
                        name="Lastname"
                        aria-describedby="Nachname"
                        value={contact.Lastname || ""}
                        onChange={handleUserInput}
                        isInvalid={!!errors.Lastname}
                    />
                </Col>          
                <Col className="mt-4 mt-md-3">
                    <Label text="E-Mail" />
                    <Form.Control
                        type="email"
                        name="Email"
                        aria-describedby="E-Mail"
                        value={contact.Email || ""}
                        onChange={handleUserInput}
                        isInvalid={!!errors.Email}
                    />
                </Col>
                <Col className="mt-4 mt-md-3">
                <Label text="Telefon" />
                    <Form.Control
                        type="text"
                        name="Phone"
                        aria-describedby="Telefon"
                        value={contact.Phone || ""}
                        onChange={handleUserInput}
                        isInvalid={!!errors.Phone}
                    />
                </Col>
            </Row>

            <Row className="mb-1">
                <Col xs={12}>
                    <Label className="fw-semibold mb-1" text="Was besitzen Sie bereits?" />                    
                    <div className="d-flex flex-wrap">                        
                        <div className="d-flex gap-2 me-4">                        
                            <Switch dense onToggle={(v) => handleToggleInput("HasPhotovoltaik", v)} name="HasPhotovoltaik" initialChecked={contact.HasPhotovoltaik} />                            
                            <Label text="Photovoltaik" className="fw-normal" />
                        </div>                        
                        <div className="d-flex gap-2 me-4">                                            
                            <Switch dense onToggle={(v) => handleToggleInput("HasBatteryStorage", v)} name="HasBatteryStorage" initialChecked={contact.HasBatteryStorage} />                            
                            <Label text="Batteriespeicher" className="fw-normal" />
                        </div>                        
                        <div className="d-flex gap-2 me-4">                        
                            <Switch dense onToggle={(v) => handleToggleInput("HasWallbox", v)} name="HasWallbox" initialChecked={contact.HasWallbox} />                            
                            <Label text="Wallbox" className="fw-normal" />
                        </div>
                    </div>
                </Col>  
                { contact.HasBatteryStorage && (      
                <Col xs={12}>
                    <div className="d-flex flex-wrap align-items-center">     
                        <Label className="me-3 fw-normal" text="Welches Fabrikat nutzen Sie?" />                      
                        <div className="d-flex gap-2 me-4 align-items-center">                        
                            <input id="batterystorage_e3dc" onChange={handleUserInput} type="radio" name="BatterystorageManufactorer" value="e3dc" style={{accentColor: "#5CC244"}} 
                                defaultChecked={contact.BatterystorageManufactorer == 'e3dc'} 
                            />                                                    
                            <label className="fw-normal" htmlFor="batterystorage_e3dc" style={{fontSize:'15px', color:'#D1D7E8'}}>E3/DC Speicher</label>
                        </div> 
                        <div className="d-flex gap-2 me-4 align-items-center">                        
                            <input id="batterystorage_other" onChange={handleUserInput} type="radio" name="BatterystorageManufactorer" value="other" style={{accentColor: "#5CC244"}} 
                                defaultChecked={contact.BatterystorageManufactorer == 'other'} 
                            />                                                    
                            <label className="fw-normal" htmlFor="batterystorage_other" style={{fontSize:'15px', color:'#D1D7E8'}}>Sonstiges</label>
                        </div>   
                    </div>                                     
                </Col>
                )}
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <Label className="fs-6 mb-2 text-white" text="Woran sind Sie interessiert?" />                    
                    <Row>
                        <Col md={4} xs={12} className='mb-2 mb-md-0'>
                            <SelectableCard 
                                icon={<IconStorage />}
                                label={'Speicher'}
                                checked={contact.InterestInStorage}
                                onChange={(v) => handleToggleInput("InterestInStorage", v)} 
                            />
                        </Col>                    
                        <Col md={4} xs={12} className='mb-2 mb-md-0'>
                            <SelectableCard 
                                icon={<IconBattery />}
                                label={'Batterienachrüstung'}
                                checked={contact.InterestInBattery}
                                onChange={(v) => handleToggleInput("InterestInBattery", v)} 
                            />
                        </Col>                    
                        <Col md={4} xs={12}>
                            <SelectableCard 
                                icon={<IconWallbox />}
                                label={'Wallbox'}
                                checked={contact.InterestInWallbox}
                                onChange={(v) => handleToggleInput("InterestInWallbox", v)} 
                            />
                        </Col>                    
                    </Row>                  
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Check
                        type="checkbox"
                        name="Partner"
                        className='dense'
                        label="Sollen wir Ihre Daten an unseren zertifizierten Fachpartner in Ihrer Nähe weiterleiten, der Ihnen gerne ein unverbindliches Angebot erstellt? *"
                        checked={!!contact.Partner}
                        onChange={handleUserInput}
                        isInvalid={!!errors.Partner}
                    />

                    <Form.Check
                        type="checkbox"
                        name="Legal"
                        className='dense'
                        label={(<>Ich habe die <a href="https://www.e3dc.com/datenschutz/" target="_blank" rel="noreferrer">Datenschutzerklärung</a> gelesen, verstanden und akzeptiert. *</>)}                        
                        checked={!!contact.Legal}
                        onChange={handleUserInput}
                        isInvalid={!!errors.Legal}
                    />
                </Col>
            </Row>
            <Stack gap={3} direction="horizontal" className="mt-2 flex-column flex-md-row button-group align-items-start">
                <Link className="order-1 order-md-0 btn-previous" to="/EnergyCost"><Button variant="outline-light">Zurück</Button></Link>
                <Button onClick={checkContactForm} variant="success btn-next">Auswertung anzeigen</Button>
            </Stack>
        </Stack>
    )
}

export default Contact;